import { useCallback, useEffect, useState } from 'react';
import { http } from '../http';
import { useAuth0 } from 'Auth';
import { transformBlockListRow } from 'BlockList/utils/transformBlockListRow';
import type {
  AttributeTypeKey,
  BlockedAttribute,
  BlockListRow,
  BlockListStatusFilter,
  ReasonKey,
} from 'BlockList/types';

// OpenAPI v1.2.0 PaginationMeta shape
type PaginationMeta = {
  page: number;
  per_page: number;
  total: number;
};

type ServerResponse = {
  data: BlockedAttribute[];
  meta: PaginationMeta;
};

export type BlockListMeta = {
  currentPage: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
  offset: number;
  limit: number;
};

export type GetBlockListAttributesFilters = {
  q?: string;
  attribute_type?: AttributeTypeKey;
  reason?: ReasonKey;
  status?: BlockListStatusFilter;
  page?: number;
  per_page?: number;
};

const transformRow = transformBlockListRow;

export const getBlockListAttributes = (
  token: string,
  filters: GetBlockListAttributesFilters = {}
) => {
  const params: Record<string, string | number | boolean> = {};
  if (filters.q !== undefined && filters.q !== '') params.q = filters.q;
  if (filters.attribute_type) params.attribute_type = filters.attribute_type;
  if (filters.reason) params.reason = filters.reason;
  if (filters.status) params.status = filters.status;
  if (filters.page !== undefined) params.page = filters.page;
  if (filters.per_page !== undefined) params.per_page = filters.per_page;

  return http.get<{ data: BlockListRow[]; meta: BlockListMeta }>({
    url: '/clearcover_admin/blocklist/attributes',
    token,
    params,
    transformResponse: (
      jsonData: string | ServerResponse
    ): { data: BlockListRow[]; meta: BlockListMeta } => {
      const raw: ServerResponse =
        typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      const meta = raw.meta;
      const limit = meta.per_page;
      const offset = (meta.page - 1) * meta.per_page;
      const totalPages = Math.ceil(meta.total / meta.per_page);
      return {
        data: raw.data.map(transformRow),
        meta: {
          currentPage: meta.page,
          perPage: meta.per_page,
          totalCount: meta.total,
          totalPages,
          offset,
          limit,
        },
      };
    },
  });
};

export type UseBlockListAttributesResult = {
  data: BlockListRow[];
  meta: BlockListMeta;
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

const DEFAULT_META: BlockListMeta = {
  currentPage: 1,
  perPage: 10,
  totalCount: 0,
  totalPages: 0,
  offset: 0,
  limit: 10,
};

export const useBlockListAttributes = (
  filters: GetBlockListAttributesFilters
): UseBlockListAttributesResult => {
  const { getToken } = useAuth0();
  // Destructure so the effect depends on stable primitives instead of
  // the `filters` object reference (which is fresh every render and
  // would cause an infinite refetch loop if included whole).
  const { q, attribute_type, reason, status, page, per_page } = filters;
  const [data, setData] = useState<BlockListRow[]>([]);
  const [meta, setMeta] = useState<BlockListMeta>(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const response = await getBlockListAttributes(token, {
          q,
          attribute_type,
          reason,
          status,
          page,
          per_page,
        });

        if (!cancelled) {
          if (response && response.status === 200 && response.data) {
            setData(response.data.data);
            setMeta(response.data.meta);
          } else {
            setError('Failed to load block list attributes.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load block list attributes.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetch();

    return () => {
      cancelled = true;
    };
  }, [q, attribute_type, reason, status, page, per_page, fetchCount, getToken]);

  return { data, meta, loading, error, refetch };
};
